import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { registerSchema } from '@dongtan/shared';
import { useAuthStore } from '@/stores/authStore';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import SocialLoginButtons from '@/components/auth/SocialLoginButtons';
import { toast } from 'sonner';

const registerFormSchema = registerSchema
  .extend({
    confirmPassword: z.string().min(1, '비밀번호 확인을 입력해주세요.'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: '비밀번호가 일치하지 않습니다.',
    path: ['confirmPassword'],
  });

type RegisterFormInput = z.infer<typeof registerFormSchema>;

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register: signUp, isLoading } = useAuthStore();
  const [serverError, setServerError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormInput>({
    resolver: zodResolver(registerFormSchema),
  });

  const onSubmit = async (data: RegisterFormInput) => {
    setServerError('');
    try {
      await signUp(data.email, data.password, data.name, data.phone);
      toast.success('회원가입이 완료되었습니다.');
      navigate('/onboarding', { replace: true });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ||
        '회원가입에 실패했습니다.';
      setServerError(message);
    }
  };

  return (
    <div>
      <h1 className="mb-2 text-2xl font-bold text-gray-900">회원가입</h1>
      <p className="mb-8 text-sm text-gray-500">
        이미 계정이 있으신가요?{' '}
        <Link to="/login" className="font-medium text-primary-600 hover:text-primary-700">
          로그인
        </Link>
      </p>

      {serverError && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600">{serverError}</div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="이름"
          placeholder="홍길동"
          error={errors.name?.message}
          {...register('name')}
        />

        <Input
          label="이메일"
          type="email"
          placeholder="email@example.com"
          error={errors.email?.message}
          {...register('email')}
        />

        <Input
          label="전화번호 (선택)"
          type="tel"
          placeholder="010-1234-5678"
          error={errors.phone?.message}
          {...register('phone')}
        />

        <Input
          label="비밀번호"
          type="password"
          placeholder="영문 + 숫자 포함 8자 이상"
          error={errors.password?.message}
          {...register('password')}
        />

        <Input
          label="비밀번호 확인"
          type="password"
          placeholder="비밀번호를 다시 입력하세요"
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />

        <Button type="submit" className="w-full" size="lg" isLoading={isLoading}>
          회원가입
        </Button>
      </form>

      <p className="mt-6 text-center text-xs text-gray-400">
        가입 시{' '}
        <a href="#" className="underline hover:text-gray-600">이용약관</a> 및{' '}
        <a href="#" className="underline hover:text-gray-600">개인정보처리방침</a>에 동의합니다.
      </p>

      <SocialLoginButtons />
    </div>
  );
}
